import type { ReactNode } from 'react';

interface Option<T extends string> {
  value: T;
  label: ReactNode;
  badge?: ReactNode;
}

interface Props<T extends string> {
  value: T;
  options: Option<T>[];
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
  block?: boolean;
  ariaLabel?: string;
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  size = 'md',
  block,
  ariaLabel,
}: Props<T>) {
  const sizeCls =
    size === 'sm'
      ? 'p-0.5 text-[11px]'
      : 'p-1 text-xs';
  const btnSize =
    size === 'sm' ? 'px-2 py-0.5' : 'px-3 py-1.5';
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`inline-flex rounded-xl border border-ink-200 font-semibold dark:border-ink-700 ${sizeCls} ${block ? 'w-full' : ''}`}
    >
      {options.map(o => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg transition ${btnSize} ${
              active
                ? 'bg-ink-900 text-white shadow-sm dark:bg-ink-50 dark:text-ink-900'
                : 'text-ink-600 hover:bg-ink-100/60 dark:text-ink-300 dark:hover:bg-ink-700'
            }`}
          >
            {o.label}
            {o.badge}
          </button>
        );
      })}
    </div>
  );
}
